import GridLayout, { WidthProvider } from "react-grid-layout";
import { PlaceholderTile } from "./PlaceholderTile";
import { CardTile } from "./CardTiles/CardTile";
import { useDashboardDomainContext } from "../../../DashboardDomainProvider";
import { useCardLayoutContext } from "../../../contexts/CardLayoutContext";
import { BoundingBox } from "./BoundingBox";
import { useCorePermissions } from "spark-core-dx/hooks";
import { useMemo, useRef } from "react";

const ResponsiveGridLayout = WidthProvider(GridLayout);

// Coerce numeric-looking strings to numbers; leave others as-is (NaN if invalid)
const toNum = (v) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : NaN;
  }
  return NaN;
};

// Convert only types (no clamping/defaulting). If any required field is NaN, caller will reject.
const coerceItemNumbers = (it, idx) => {
  const w = toNum(it.w);
  const h = toNum(it.h);
  const x = toNum(it.x);
  const y = toNum(it.y);
  return {
    ...it,
    i: String(it.i ?? it.id ?? idx),
    w, h, x, y,
  };
};

export const VisualGridBody = () => {
  const { isViewMode } = useDashboardDomainContext();

  const {
    containerRef,
    columnCount,
    rowHeight,
    layout,
    handleCardDragStop,
    handleCardResizeStart,
    handleCardResizeStop
  } = useCardLayoutContext();

  const { canEdit } = useCorePermissions();

  const lastGoodLayoutRef = useRef([]);

  // Keep numeric types stable; if anything invalid, use last good (no size/position changes).
  const safeLayout = useMemo(() => {
    if (!Array.isArray(layout)) return lastGoodLayoutRef.current;

    const coerced = layout.map(coerceItemNumbers);
    const allFinite = coerced.every(
      (l) =>
        Number.isFinite(l.w) &&
        Number.isFinite(l.h) &&
        Number.isFinite(l.x) &&
        Number.isFinite(l.y)
    );

    if (allFinite) {
      // commit exactly what came in (with only type coercion), preserving sizes/positions
      lastGoodLayoutRef.current = coerced;
      return coerced;
    }

    // If invalid this frame, keep prior good layout to avoid RGL NaN warning and UI jumps
    return lastGoodLayoutRef.current;
  }, [layout]);

  return (
    <div
      className={`visual-grid-body ${isViewMode ? 'view-mode' : canEdit('Dashboard') ? 'design-mode' : ''}`}
      ref={containerRef}
    >
      <ResponsiveGridLayout
        className={"visual-responsive-grid-layout"}
        layout={safeLayout}
        cols={columnCount}
        rowHeight={rowHeight}
        width={containerRef?.current?.offsetWidth ?? 0}
        margin={[0, 0]}
        compactType={null}
        preventCollision={false}
        isDraggable={true}
        isResizable={true}
        resizeHandles={isViewMode ? [] : ['se', 'sw', 'ne', 'nw', 'n', 'e', 's', 'w']}
        allowOverlap={true}
        onDragStop={handleCardDragStop}
        onResizeStart={handleCardResizeStart}
        onResizeStop={handleCardResizeStop}
      >
        {safeLayout.map(item =>
          item.i?.startsWith('ph') ? (
            <PlaceholderTile key={item.i} item={item} />
          ) : item.i?.startsWith('bounding-box') ? (
            <BoundingBox key={item.i} boundingBox={item} />
          ) : (
            <CardTile key={item.i} card={item} />
          )
        )}
      </ResponsiveGridLayout>
    </div>
  );
};
